/* ============================================
   ContentCalendar — Monthly + Weekly calendar
   Shows posts per day with navigation, detail
   modal, and proposal approval bar.
   ============================================ */
import { useState, useMemo, useRef, useEffect } from 'react'
import type { SocialPost } from '@/lib/types'
import PostCard from '@/components/social-media/PostCard'
import PostDetailModal from '@/components/social-media/PostDetailModal'

interface ContentCalendarProps {
  posts: SocialPost[]
  onSelectPost: (post: SocialPost) => void
  onSelectDate: (date: Date) => void
  onApproveProposals?: () => void
  onRejectProposals?: () => void
  onGenerateImages?: () => void
  onOpenVisualReview?: () => void
  onPublishToMetricool?: () => void
  isGeneratingImages?: boolean
}

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const DAY_LABELS = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SÁB', 'DOM']
const DAY_LABELS_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']

type CalendarView = 'month' | 'week'

function getCalendarDays(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1)
  const last = new Date(year, month + 1, 0)
  const startDay = (first.getDay() + 6) % 7 // Monday = 0
  const days: (Date | null)[] = Array(startDay).fill(null)
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d))
  }
  while (days.length % 7 !== 0) days.push(null)
  return days
}

function getWeekDays(baseDate: Date): Date[] {
  const d = new Date(baseDate)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Monday
  const monday = new Date(d.setDate(diff))
  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    days.push(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i))
  }
  return days
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatWeekRange(days: Date[]): string {
  const first = days[0]
  const last = days[6]
  const f = first.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' })
  const l = last.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' })
  return `${f} — ${l}, ${last.getFullYear()}`
}

export default function ContentCalendar({ posts, onSelectPost, onSelectDate, onApproveProposals, onRejectProposals, onGenerateImages, onOpenVisualReview, onPublishToMetricool, isGeneratingImages }: ContentCalendarProps) {
  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [view, setView] = useState<CalendarView>('week')
  const [weekBase, setWeekBase] = useState(today)
  const [detailPost, setDetailPost] = useState<SocialPost | null>(null)

  const calendarDays = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])
  const weekDays = useMemo(() => getWeekDays(weekBase), [weekBase])

  // Group posts by date
  const postsByDate = useMemo(() => {
    const map = new Map<string, SocialPost[]>()
    for (const post of posts) {
      if (!post.publication_date) continue
      const d = new Date(post.publication_date)
      const key = dateKey(d)
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(post)
    }
    // Sort posts chronologically within each day
    for (const [, dayPosts] of map) {
      dayPosts.sort((a, b) =>
        new Date(a.publication_date!).getTime() - new Date(b.publication_date!).getTime()
      )
    }
    return map
  }, [posts])

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else { setViewMonth(m => m - 1) }
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else { setViewMonth(m => m + 1) }
  }
  const prevWeek = () => {
    const d = new Date(weekBase)
    d.setDate(d.getDate() - 7)
    setWeekBase(d)
  }
  const nextWeek = () => {
    const d = new Date(weekBase)
    d.setDate(d.getDate() + 7)
    setWeekBase(d)
  }
  const goToday = () => {
    setViewYear(today.getFullYear())
    setViewMonth(today.getMonth())
    setWeekBase(today)
  }

  // Count proposals across ALL months (global)
  const allProposals = posts.filter(p => p.status === 'proposal')
  const proposalCount = allProposals.length

  // Auto-navigate to proposals month on first load
  const hasAutoNavigated = useRef(false)
  useEffect(() => {
    if (!hasAutoNavigated.current && allProposals.length > 0) {
      hasAutoNavigated.current = true
      const firstProposal = allProposals
        .filter(p => p.publication_date)
        .sort((a, b) => new Date(a.publication_date!).getTime() - new Date(b.publication_date!).getTime())[0]
      if (firstProposal?.publication_date) {
        const d = new Date(firstProposal.publication_date)
        if (d.getFullYear() !== viewYear || d.getMonth() !== viewMonth) {
          setViewYear(d.getFullYear())
          setViewMonth(d.getMonth())
        }
        setWeekBase(d)
      }
    }
  }, [allProposals.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Stats for header (month-scoped)
  const monthPosts = posts.filter(p => {
    if (!p.publication_date) return false
    const d = new Date(p.publication_date)
    return d.getFullYear() === viewYear && d.getMonth() === viewMonth
  })
  const publishedCount = monthPosts.filter(p => p.status === 'published').length
  const scheduledCount = monthPosts.filter(p => p.status === 'scheduled').length
  const draftCount = monthPosts.filter(p => p.status === 'draft').length
  const monthProposalCount = monthPosts.filter(p => p.status === 'proposal').length

  // Handle post click — open detail modal
  const handlePostClick = (post: SocialPost) => {
    setDetailPost(post)
  }

  return (
    <div className="sm-calendar">
      {/* Calendar header */}
      <div className="sm-calendar__header">
        <div className="sm-calendar__nav">
          <button className="btn btn-secondary btn-sm" onClick={view === 'month' ? prevMonth : prevWeek} type="button" title="Anterior">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 className="sm-calendar__title">
            {view === 'month'
              ? `${MONTH_NAMES[viewMonth]} ${viewYear}`
              : formatWeekRange(weekDays)}
          </h2>
          <button className="btn btn-secondary btn-sm" onClick={view === 'month' ? nextMonth : nextWeek} type="button" title="Siguiente">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="btn btn-secondary btn-sm" onClick={goToday} type="button" style={{ marginLeft: 8 }}>
            Hoy
          </button>

          {/* View toggle */}
          <div className="sm-calendar__view-toggle">
            <button
              type="button"
              className={`sm-calendar__view-btn ${view === 'month' ? 'sm-calendar__view-btn--active' : ''}`}
              onClick={() => setView('month')}
              title="Vista mensual"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="10" y1="4" x2="10" y2="22"/><line x1="3" y1="16" x2="21" y2="16"/>
              </svg>
            </button>
            <button
              type="button"
              className={`sm-calendar__view-btn ${view === 'week' ? 'sm-calendar__view-btn--active' : ''}`}
              onClick={() => { setView('week'); setWeekBase(new Date(viewYear, viewMonth, today.getDate())) }}
              title="Vista semanal"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="sm-calendar__stats">
          <span className="sm-calendar__stat">
            <span className="sm-calendar__stat-dot" style={{ background: 'var(--success)' }} />
            {publishedCount} publicados
          </span>
          <span className="sm-calendar__stat">
            <span className="sm-calendar__stat-dot" style={{ background: 'var(--primary)' }} />
            {scheduledCount} programados
          </span>
          <span className="sm-calendar__stat">
            <span className="sm-calendar__stat-dot" style={{ background: 'var(--text-secondary)' }} />
            {draftCount} borradores
          </span>
          {monthProposalCount > 0 && (
            <span className="sm-calendar__stat">
              <span className="sm-calendar__stat-dot" style={{ background: 'var(--primary-light)' }} />
              {monthProposalCount} propuestas
            </span>
          )}
        </div>
      </div>

      {/* ── Pipeline Action Bars ── */}

      {/* Step 1: Proposal approval */}
      {proposalCount > 0 && (
        <div className="sm-calendar__pipeline-bar sm-calendar__pipeline-bar--proposal">
          <span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            {proposalCount} propuestas de Marco pendientes de aprobación
          </span>
          <div className="sm-calendar__pipeline-bar-actions">
            <button type="button" className="btn btn-sm btn-primary" onClick={onApproveProposals}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><polyline points="20 6 9 17 4 12"/></svg>
              Aprobar todas
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={onRejectProposals} style={{ color: 'var(--danger)' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Rechazar
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Generate images for drafts */}
      {(() => {
        const draftsNeedingImage = posts.filter(p => p.status === 'draft' && p.image_prompt && (!p.media_urls || p.media_urls.length === 0))
        const totalWithPrompt = posts.filter(p => p.image_prompt).length
        const generated = posts.filter(p => p.status === 'pending_review').length

        // Show if there are drafts needing images OR generation is actively running
        if (draftsNeedingImage.length === 0 && !isGeneratingImages) return null

        const progressText = isGeneratingImages
          ? `Generando imágenes con Gemini AI... ${generated}/${draftsNeedingImage.length + generated} listas`
          : `${draftsNeedingImage.length} borradores listos para generar imágenes`

        return (
          <div className={`sm-calendar__pipeline-bar sm-calendar__pipeline-bar--generate ${isGeneratingImages ? 'sm-calendar__pipeline-bar--loading' : ''}`}>
            <span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
              </svg>
              {progressText}
            </span>
            <div className="sm-calendar__pipeline-bar-actions">
              {isGeneratingImages ? (
                <span className="sm-calendar__pipeline-bar-progress">
                  <div className="auth-spinner" style={{ width: 14, height: 14, marginRight: 6 }} />
                  {Math.round((generated / Math.max(totalWithPrompt, 1)) * 100)}%
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={onGenerateImages}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                    <path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/>
                  </svg>
                  Generar Imágenes
                </button>
              )}
            </div>
          </div>
        )
      })()}

      {/* Step 3: Visual review */}
      {(() => {
        const pendingReview = posts.filter(p => p.status === 'pending_review')
        if (pendingReview.length === 0) return null
        return (
          <div className="sm-calendar__pipeline-bar sm-calendar__pipeline-bar--review">
            <span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
              {pendingReview.length} posts con imagen listos para revisión visual
            </span>
            <div className="sm-calendar__pipeline-bar-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={onOpenVisualReview}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                Revisar Imágenes
              </button>
            </div>
          </div>
        )
      })()}

      {/* Step 4: Publish to Metricool */}
      {(() => {
        const approved = posts.filter(p => p.status === 'approved')
        if (approved.length === 0) return null
        return (
          <div className="sm-calendar__pipeline-bar sm-calendar__pipeline-bar--publish">
            <span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              {approved.length} posts aprobados listos para enviar a Metricool
            </span>
            <div className="sm-calendar__pipeline-bar-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={onPublishToMetricool}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
                Enviar a Metricool
              </button>
            </div>
          </div>
        )
      })()}

      {/* ────── MONTH VIEW ────── */}
      {view === 'month' && (
        <>
          <div className="sm-calendar__grid sm-calendar__grid--header">
            {DAY_LABELS.map(d => (
              <div key={d} className="sm-calendar__day-header">{d}</div>
            ))}
          </div>
          <div className="sm-calendar__grid">
            {calendarDays.map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} className="sm-calendar__cell sm-calendar__cell--empty" />

              const key = dateKey(day)
              const dayPosts = postsByDate.get(key) ?? []
              const isToday = isSameDay(day, today)
              const maxShow = 3
              const overflow = dayPosts.length > maxShow ? dayPosts.length - maxShow : 0

              return (
                <div
                  key={key}
                  className={`sm-calendar__cell ${isToday ? 'sm-calendar__cell--today' : ''} ${dayPosts.length > 0 ? 'sm-calendar__cell--has-posts' : ''}`}
                  onClick={() => { if (dayPosts.length === 0) onSelectDate(day) }}
                >
                  <div className="sm-calendar__cell-header">
                    <span className={`sm-calendar__day-num ${isToday ? 'sm-calendar__day-num--today' : ''}`}>
                      {day.getDate()}
                    </span>
                  </div>
                  <div className="sm-calendar__cell-posts">
                    {dayPosts.slice(0, maxShow).map(post => (
                      <PostCard key={post.id} post={post} compact onClick={() => handlePostClick(post)} />
                    ))}
                    {overflow > 0 && (
                      <button
                        className="sm-calendar__overflow"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handlePostClick(dayPosts[maxShow]) }}
                      >
                        +{overflow} más
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* ────── WEEK VIEW ────── */}
      {view === 'week' && (
        <div className="sm-calendar__week">
          {weekDays.map((day, idx) => {
            const key = dateKey(day)
            const dayPosts = postsByDate.get(key) ?? []
            const isToday = isSameDay(day, today)

            return (
              <div
                key={key}
                className={`sm-calendar__week-col ${isToday ? 'sm-calendar__week-col--today' : ''}`}
              >
                <div className="sm-calendar__week-header">
                  <span className="sm-calendar__week-day">{DAY_LABELS_FULL[idx]}</span>
                  <span className={`sm-calendar__week-num ${isToday ? 'sm-calendar__day-num--today' : ''}`}>
                    {day.getDate()}
                  </span>
                </div>
                <div className="sm-calendar__week-posts">
                  {dayPosts.length === 0 && (
                    <button
                      type="button"
                      className="sm-calendar__week-empty"
                      onClick={() => onSelectDate(day)}
                    >
                      + Agregar
                    </button>
                  )}
                  {dayPosts.map(post => (
                    <PostCard key={post.id} post={post} onClick={() => handlePostClick(post)} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Post Detail Modal */}
      {detailPost && (
        <PostDetailModal post={detailPost} onClose={() => setDetailPost(null)} onEdit={onSelectPost} />
      )}
    </div>
  )
}
